/**
 * Service for managing vehicle types and body types
 * Uses explicit API versioning - endpoints on v2
 */

import { buildApiUrl } from '@/utils/apiHelpers';

export interface VehicleType {
  id: string;
  typeName: string;
  typeCode: string;
  description: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface VehicleBodyType {
  id: string;
  bodyName: string;
  bodyCode: string;
  description: string;
  typicalSeating: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateVehicleTypeRequest {
  name: string;
  description?: string;
  displayOrder?: number;
}

export interface UpdateVehicleTypeRequest {
  name?: string;
  description?: string;
  displayOrder?: number;
  isActive?: boolean;
}

export interface CreateVehicleBodyTypeRequest {
  name: string;
  description?: string;
  typicalSeatingCapacity?: number;
  displayOrder?: number;
}

export interface UpdateVehicleBodyTypeRequest {
  name?: string;
  description?: string;
  typicalSeatingCapacity?: number;
  displayOrder?: number;
  isActive?: boolean;
}

class VehicleTypesService {
  private getHeaders() {
    return {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    };
  }

  // ==================== VEHICLE TYPES ====================

  async getVehicleTypes(): Promise<VehicleType[]> {
    const response = await fetch(buildApiUrl('/admin/vehicle-types', 'v1'), {
      headers: this.getHeaders()
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch vehicle types' }));
      throw new Error(error.message || 'Failed to fetch vehicle types');
    }

    const result = await response.json();

    // Handle different response structures
    // 1. Direct array: [...]
    if (Array.isArray(result)) {
      return result;
    }

    // 2. Wrapped in success object: { success: true, data: {...} }
    if (result.success && result.data) {
      const data = result.data;
      // 3. data might be an array or an object with vehicleTypes key
      if (Array.isArray(data)) {
        return data;
      }
      // 4. data is object: { vehicleTypes: [...] }
      return data.vehicleTypes || data.types || [];
    }

    // 5. Direct object: { vehicleTypes: [...] }
    return result.vehicleTypes || result.data || [];
  }

  async createVehicleType(data: CreateVehicleTypeRequest): Promise<VehicleType> {
    const response = await fetch(buildApiUrl('/admin/vehicle-types', 'v1'), {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to create vehicle type' }));
      throw new Error(error.message || 'Failed to create vehicle type');
    }

    return response.json();
  }

  async updateVehicleType(id: string, data: UpdateVehicleTypeRequest): Promise<VehicleType> {
    const response = await fetch(buildApiUrl(`/admin/vehicle-types/${id}`, 'v1'), {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to update vehicle type' }));
      throw new Error(error.message || 'Failed to update vehicle type');
    }

    return response.json();
  }

  async deleteVehicleType(id: string): Promise<void> {
    const response = await fetch(buildApiUrl(`/admin/vehicle-types/${id}`, 'v1'), {
      method: 'DELETE',
      headers: this.getHeaders()
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to delete vehicle type' }));
      throw new Error(error.message || 'Failed to delete vehicle type');
    }
  }

  // ==================== VEHICLE BODY TYPES ====================

  async getVehicleBodyTypes(): Promise<VehicleBodyType[]> {
    const response = await fetch(buildApiUrl('/admin/vehicle-body-types', 'v1'), {
      headers: this.getHeaders()
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch vehicle body types' }));
      throw new Error(error.message || 'Failed to fetch vehicle body types');
    }

    const result = await response.json();

    // Handle different response structures
    // 1. Direct array: [...]
    if (Array.isArray(result)) {
      return result;
    }

    // 2. Wrapped in success object: { success: true, data: {...} }
    if (result.success && result.data) {
      const data = result.data;
      // 3. data might be an array or an object with bodyTypes key
      if (Array.isArray(data)) {
        return data;
      }
      // 4. data is object: { bodyTypes: [...] }
      return data.bodyTypes || data.vehicleBodyTypes || data.types || [];
    }

    // 5. Direct object: { bodyTypes: [...] }
    return result.bodyTypes || result.vehicleBodyTypes || result.data || [];
  }

  async createVehicleBodyType(data: CreateVehicleBodyTypeRequest): Promise<VehicleBodyType> {
    const response = await fetch(buildApiUrl('/admin/vehicle-body-types', 'v1'), {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to create vehicle body type' }));
      throw new Error(error.message || 'Failed to create vehicle body type');
    }

    return response.json();
  }

  async updateVehicleBodyType(id: string, data: UpdateVehicleBodyTypeRequest): Promise<VehicleBodyType> {
    const response = await fetch(buildApiUrl(`/admin/vehicle-body-types/${id}`, 'v1'), {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to update vehicle body type' }));
      throw new Error(error.message || 'Failed to update vehicle body type');
    }

    return response.json();
  }

  async deleteVehicleBodyType(id: string): Promise<void> {
    const response = await fetch(buildApiUrl(`/admin/vehicle-body-types/${id}`, 'v1'), {
      method: 'DELETE',
      headers: this.getHeaders()
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to delete vehicle body type' }));
      throw new Error(error.message || 'Failed to delete vehicle body type');
    }
  }
}

export const vehicleTypesService = new VehicleTypesService();
